import { createSelector } from 'reselect';

import { userRoles } from '../../../shared/authorization';
import { YES_FLAG } from '../../../shared/constants';

export const beginningBalancesStateSelector = state => state.administration.beginningBalances;

export const adjustmentPeriodsOptionsSelector = createSelector(
  state => state.administration.beginningBalances.adjustmentPeriods,
  adjustmentPeriods => adjustmentPeriods.map(({ periodName }) => ({ label: periodName, value: periodName }))
);

export const adjustmentPeriodSelector = state => state.administration.beginningBalances.adjustmentPeriod;

export const companyCodesSelector = createSelector(
  state => state.administration.beginningBalances.companyCodes,
  companyCodes => companyCodes.map(companyCode => ({
    label: companyCode.companyCodeDescription,
    value: companyCode.companyCode,
  }))
);

export const companyCodeSelector = state => state.administration.beginningBalances.companyCode;

export const connectionsSelector = createSelector(
  state => state.administration.beginningBalances.connections,
  connections => connections && connections.map(connection => ({
    label: connection.connection,
    value: connection.connection,
  }))
);

export const isLoadingCompanyCodesSelector = createSelector(
  beginningBalancesStateSelector,
  ({ companyCodesLoading }) => companyCodesLoading
);

export const isLoadingConnectionsSelector = createSelector(
  beginningBalancesStateSelector,
  ({ connectionsLoading }) => connectionsLoading
);

export const isLoadingCompanyCodesForConnectionSelector = createSelector(
  beginningBalancesStateSelector,
  ({ companyCodesForConnectionLoading }) => companyCodesForConnectionLoading
);

export const connectionSelector = state => state.administration.beginningBalances.connection;

export const companyCodeDataSelector = createSelector(
  [
    state => state.administration.beginningBalances.companyCodes,
    state => state.administration.beginningBalances.companyCode,
    state => state.administration.beginningBalances.companyCodeForConnection,
  ],
  (companyCodes, companyCode, companyCodeForConnection) =>
    companyCode
      ? companyCodes.find(cc => cc.companyCode === companyCode)
      : companyCodes.find(cc => cc.companyCode === companyCodeForConnection)
);

export const companyCodesForConnectionForDropdownSelector = createSelector(
  state => state.administration.beginningBalances.companyCodesForConnection,
  companyCodes => companyCodes && companyCodes.map(companyCode => ({
    label: `${companyCode.companyCode} - ${companyCode.companyCodeDescription}`,
    value: companyCode.companyCode,
  }))
);

export const companyCodeForConnectionSelector = state =>
  state.administration.beginningBalances.companyCodeForConnection;

export const beginningBalancesSelector = state => state.administration.beginningBalances.beginningBalances;
export const beginningBalancesLoadingSelector = state =>
  state.administration.beginningBalances.beginningBalancesLoading;

export const beginningBalancesImportDataSelector = state => state.administration.beginningBalances.importData;

export const beginningBalancesIgnoredCompanyCodesSelector = state =>
  state.administration.beginningBalances.ignoredCompanyCodes;
export const validationSummarySelector = state => state.administration.beginningBalances.validationSummary;

export const currentAdjustmentPeriodSelector = createSelector(
  state => state.administration.beginningBalances.adjustmentPeriods,
  periods => periods.find(period => period.currentPeriod === YES_FLAG)
);

export const cannotImportBeginningBalancesSelector = createSelector(
  adjustmentPeriodSelector,
  currentAdjustmentPeriodSelector,
  ({ user }) => user.user.role === userRoles.SUPER_USER,
  (adjustmentPeriod, currentAdjustmentPeriod, isSuperUser ) => {
    const isCurrentPeriod = adjustmentPeriod
      && currentAdjustmentPeriod
      && (adjustmentPeriod === currentAdjustmentPeriod.periodName);
    return !isCurrentPeriod && !isSuperUser;
  }
);

export const isUploadingBeginningBalancesSelector = state => (
  state.administration.beginningBalances.isUploadingBeginningBalances
);

export const companyCodeWithCurrencySelector = createSelector(
  companyCodeDataSelector,
  ({ shared }) => shared.companyCodes,
  (companyCodeData, sharedCompanyCodes) => {
    if (!companyCodeData) {
      return null;
    }

    const sharedCompanyData = sharedCompanyCodes.find(({ cc }) => cc === companyCodeData.companyCode);

    if (!sharedCompanyData) {
      return companyCodeData;
    }

    return {
      ...companyCodeData,
      functionalCurrency: sharedCompanyData.functionalCurrency,
    };
  }
);

export const companyCodesForConnectionSelector = ({ administration }) =>
  administration.beginningBalances.companyCodesForConnection;
